import React, { useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EditOutlined,
  SaveOutlined,
  SendOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import managementReviewService, {
  type MrmAction,
  type MrmTopic,
} from '@/services/managementReview.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import {
  ENTITY_TYPE_MANAGEMENT_REVIEW_AGENDA,
  ENTITY_TYPE_MANAGEMENT_REVIEW_MINUTES,
} from '@/constants/approval-entity-types';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_MANAGEMENT_REVIEW } from '@/constants/document-entity-types';
import { useAuthStore } from '@/store/authStore';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

const { Title, Text } = Typography;
const { TextArea } = Input;

const statusColor = (status: string) => {
  const map: Record<string, string> = {
    Draft: 'default',
    'Pending Agenda Approval': 'processing',
    'Agenda Approved': 'blue',
    'Minutes In Progress': 'cyan',
    'Pending Minutes Approval': 'orange',
    Closed: 'success',
  };
  return map[status] || 'default';
};

const ManagementReviewDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const perms = (useAuthStore((s) => s.user?.permissions) ?? []) as string[];
  const canEdit =
    perms.includes('quality.management_review.edit') ||
    perms.includes('quality.management_review.create') ||
    perms.length === 0;
  const canSubmit =
    perms.includes('quality.management_review.submit') ||
    perms.includes('quality.management_review.edit') ||
    perms.length === 0;

  const [minutesForm] = Form.useForm();
  const [actionsForm] = Form.useForm();
  const [topicsForm] = Form.useForm();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['management-reviews', id],
    queryFn: () => managementReviewService.get(id!),
    enabled: !!id,
  });

  const { data: related } = useQuery({
    queryKey: ['management-reviews', 'related-summary'],
    queryFn: () => managementReviewService.relatedSummary(),
  });

  useEffect(() => {
    if (!data) return;
    minutesForm.setFieldsValue({
      meeting_date: data.meeting_date ? dayjs(data.meeting_date) : undefined,
      place_of_meeting: data.place_of_meeting || data.venue || undefined,
      chaired_by_name: data.chaired_by_name || undefined,
      chaired_by_designation: data.chaired_by_designation || undefined,
      next_mrm_date: data.next_mrm_date ? dayjs(data.next_mrm_date) : undefined,
      qms_effectiveness_summary: data.qms_effectiveness_summary || undefined,
      minutes_updated_by_name: data.minutes_updated_by_name || undefined,
      minutes_updated_by_designation: data.minutes_updated_by_designation || undefined,
      minutes_updated_date: data.minutes_updated_date ? dayjs(data.minutes_updated_date) : undefined,
      attendees: (data.participants || []).map((p, i) => ({
        key: p.id || `a-${i}`,
        id: p.id,
        name: p.name,
        designation: p.designation || '',
        attended: Number(p.attended) === 1,
        participant_role: p.participant_role || 'attendee',
      })),
    });
    topicsForm.setFieldsValue({
      topics: (data.topics || []).map((t) => ({
        id: t.id,
        topic_code: t.topic_code,
        title: t.title,
        included_on_agenda: Number(t.included_on_agenda) !== 0,
        overview_comments: t.overview_comments || '',
        action_planned: t.action_planned || '',
        action_target_date: t.action_target_date ? dayjs(t.action_target_date) : undefined,
        action_responsibility: t.action_responsibility || '',
        additional_remarks: t.additional_remarks || '',
      })),
    });
    actionsForm.setFieldsValue({
      actions: (data.actions || []).map((a, i) => ({
        key: a.id || `x-${i}`,
        id: a.id,
        description: a.description,
        owner_name: a.owner_name || '',
        due_date: a.due_date ? dayjs(a.due_date) : undefined,
        status: a.status || 'Open',
        effectiveness_notes: a.effectiveness_notes || '',
        capa_id: a.capa_id || null,
        source_topic_id: a.source_topic_id || null,
      })),
    });
  }, [data, minutesForm, topicsForm, actionsForm]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['management-reviews'] });
    qc.invalidateQueries({ queryKey: ['management-reviews', id] });
  };

  const saveMinutes = useMutation({
    mutationFn: async () => {
      const v = await minutesForm.validateFields();
      const t = await topicsForm.validateFields();
      return managementReviewService.update(id!, {
        meeting_date: v.meeting_date ? v.meeting_date.format('YYYY-MM-DD') : null,
        place_of_meeting: v.place_of_meeting || null,
        chaired_by_name: v.chaired_by_name || null,
        chaired_by_designation: v.chaired_by_designation || null,
        next_mrm_date: v.next_mrm_date ? v.next_mrm_date.format('YYYY-MM-DD') : null,
        qms_effectiveness_summary: v.qms_effectiveness_summary || null,
        minutes_updated_by_name: v.minutes_updated_by_name || null,
        minutes_updated_by_designation: v.minutes_updated_by_designation || null,
        minutes_updated_date: v.minutes_updated_date
          ? v.minutes_updated_date.format('YYYY-MM-DD')
          : null,
        participants: (v.attendees || []).map((p: any, i: number) => ({
          id: p.id,
          name: p.name,
          designation: p.designation || null,
          participant_role: 'attendee',
          attended: !!p.attended,
          sort_order: i,
        })),
        topics: (t.topics || []).map((topic: any) => ({
          id: topic.id,
          included_on_agenda: !!topic.included_on_agenda,
          overview_comments: topic.overview_comments || null,
          action_planned: topic.action_planned || null,
          action_target_date: topic.action_target_date
            ? topic.action_target_date.format('YYYY-MM-DD')
            : null,
          action_responsibility: topic.action_responsibility || null,
          additional_remarks: topic.additional_remarks || null,
        })),
      });
    },
    onSuccess: () => {
      message.success('Minutes saved');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const saveActions = useMutation({
    mutationFn: async () => {
      const v = await actionsForm.validateFields();
      return managementReviewService.update(id!, {
        actions: (v.actions || [])
          .filter((a: any) => String(a.description || '').trim())
          .map((a: any, i: number) => ({
            id: a.id,
            description: a.description,
            owner_name: a.owner_name || null,
            due_date: a.due_date ? a.due_date.format('YYYY-MM-DD') : null,
            status: a.status === 'Closed' ? 'Closed' : 'Open',
            effectiveness_notes: a.effectiveness_notes || null,
            source_topic_id: a.source_topic_id || null,
            sort_order: i,
          })),
      });
    },
    onSuccess: () => {
      message.success('Actions saved');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const submitAgenda = useMutation({
    mutationFn: () => managementReviewService.submitAgenda(id!),
    onSuccess: () => {
      message.success('Agenda submitted for approval');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const startMinutes = useMutation({
    mutationFn: () => managementReviewService.startMinutes(id!),
    onSuccess: () => {
      message.success('Minutes started');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const submitMinutes = useMutation({
    mutationFn: () => managementReviewService.submitMinutes(id!),
    onSuccess: () => {
      message.success('Minutes submitted for approval');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const createCapa = useMutation({
    mutationFn: async (action: MrmAction) => {
      if (!action.id) throw new Error('Save the action first');
      const responsible = window.prompt('Responsible person user id (UUID) for the CAPA:');
      if (!responsible?.trim()) throw new Error('Responsible person required');
      return managementReviewService.createCapaFromAction(id!, action.id, {
        responsible_person_id: responsible.trim(),
        description: action.description,
        action_plan: action.description,
        target_completion_date: action.due_date
          ? String(action.due_date).slice(0, 10)
          : undefined,
      });
    },
    onSuccess: () => {
      message.success('CAPA created and linked');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const minutesEditable =
    canEdit &&
    data &&
    (data.status === 'Agenda Approved' || data.status === 'Minutes In Progress');
  const agendaEditable = canEdit && data?.status === 'Draft';

  if (isLoading || !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const agendaTopics = (data.topics || []) as MrmTopic[];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/management-reviews')}>
            Back
          </Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {data.mrm_number}
            </Title>
            <Tag color={statusColor(String(data.status))}>{data.status}</Tag>
          </div>
        </Space>
        <Space wrap>
          <ReportDocumentActions
            reportKey="quality-mrm-agenda"
            params={{ id: data.id }}
            documentId={data.id}
            sendPermissions={['quality.management_review.edit', 'quality.management_review.submit']}
          />
          <ReportDocumentActions
            reportKey="quality-mrm-minutes"
            params={{ id: data.id }}
            documentId={data.id}
            sendPermissions={['quality.management_review.edit', 'quality.management_review.submit']}
          />
          {agendaEditable ? (
            <Button icon={<EditOutlined />} onClick={() => navigate(`/qms/management-reviews/${id}/edit`)}>
              Edit agenda
            </Button>
          ) : null}
          {canSubmit && data.status === 'Draft' ? (
            <Popconfirm title="Submit agenda for Top Management approval?" onConfirm={() => submitAgenda.mutate()}>
              <Button type="primary" icon={<SendOutlined />} loading={submitAgenda.isPending}>
                Submit agenda
              </Button>
            </Popconfirm>
          ) : null}
          {canEdit && data.status === 'Agenda Approved' ? (
            <Button type="primary" icon={<CheckOutlined />} loading={startMinutes.isPending} onClick={() => startMinutes.mutate()}>
              Start minutes
            </Button>
          ) : null}
          {canSubmit && (data.status === 'Minutes In Progress' || data.status === 'Agenda Approved') ? (
            <Popconfirm title="Submit minutes for Top Management approval?" onConfirm={() => submitMinutes.mutate()}>
              <Button type="primary" icon={<SendOutlined />} loading={submitMinutes.isPending}>
                Submit minutes
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Text type="secondary">Proposed</Text>
            <div>
              {data.proposed_date ? dayjs(data.proposed_date).format('DD/MM/YYYY') : '—'}{' '}
              {data.proposed_time || ''}
            </div>
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary">Venue</Text>
            <div>{data.venue || '—'}</div>
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary">Meeting date</Text>
            <div>{data.meeting_date ? dayjs(data.meeting_date).format('DD/MM/YYYY') : '—'}</div>
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary">Next MRM</Text>
            <div>{data.next_mrm_date ? dayjs(data.next_mrm_date).format('DD/MM/YYYY') : '—'}</div>
          </Col>
        </Row>
      </Card>

      <Tabs
        items={[
          {
            key: 'agenda',
            label: 'Agenda',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Card title="Required participants" size="small">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(_, i) => String(i)}
                    dataSource={(data.participants || []).filter(
                      (p) => String(p.participant_role || 'required') === 'required'
                    )}
                    columns={[
                      { title: '#', width: 50, render: (_: unknown, __: unknown, i: number) => i + 1 },
                      { title: 'Name', dataIndex: 'name' },
                      { title: 'Designation', dataIndex: 'designation' },
                    ]}
                  />
                </Card>
                <Card title="Agenda points" size="small">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={agendaTopics.filter((t) => Number(t.included_on_agenda) !== 0)}
                    columns={[
                      { title: '#', dataIndex: 'sort_order', width: 50 },
                      { title: 'Point', dataIndex: 'title' },
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: 'minutes',
            label: 'Minutes',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Card
                  size="small"
                  title="Related registers (inputs)"
                  extra={<Text type="secondary">Read-only counts — open the register for detail</Text>}
                >
                  <Space wrap>
                    <Link to="/qms/ncrs">
                      Open NCRs <Tag>{related?.open_ncrs ?? '—'}</Tag>
                    </Link>
                    <Link to="/qms/capas">
                      Open CAPAs <Tag>{related?.open_capas ?? '—'}</Tag>
                    </Link>
                    <Link to="/qms/audits">
                      Audits <Tag>{related?.audits ?? '—'}</Tag>
                    </Link>
                    <Link to="/grc/risk-assessments">
                      Risk assessments <Tag>{related?.risk_assessments ?? '—'}</Tag>
                    </Link>
                    <Link to="/scm/quality/supplier-performance">
                      Supplier performance <LinkOutlined />
                    </Link>
                    <Link to="/qms/department-kpis">
                      Department KPIs <LinkOutlined />
                    </Link>
                  </Space>
                </Card>

                <Form form={minutesForm} layout="vertical" disabled={!minutesEditable}>
                  <Card title="Meeting header" size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <Form.Item name="meeting_date" label="Meeting date" rules={[{ required: true }]}>
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="place_of_meeting" label="Place of meeting">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="next_mrm_date" label="Next MRM date (tentative)">
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="chaired_by_name" label="Chaired by (Top management)">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="chaired_by_designation" label="Designation">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name="qms_effectiveness_summary"
                          label="Summary assessment of effectiveness of QMS"
                        >
                          <TextArea rows={3} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  <Card title="Attendees" size="small" style={{ marginBottom: 16 }}>
                    <Form.List name="attendees">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <Row gutter={8} key={field.key} style={{ marginBottom: 8 }}>
                              <Col xs={24} md={8}>
                                <Form.Item {...field} name={[field.name, 'name']} rules={[{ required: true }]}>
                                  <Input placeholder="Name" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Form.Item {...field} name={[field.name, 'designation']}>
                                  <Input placeholder="Designation" />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={4}>
                                <Form.Item {...field} name={[field.name, 'attended']} valuePropName="checked">
                                  <Checkbox>Attended</Checkbox>
                                </Form.Item>
                              </Col>
                              <Col>
                                <Button danger type="link" onClick={() => remove(field.name)}>
                                  Remove
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Button type="dashed" onClick={() => add({ name: '', designation: '', attended: true })} block>
                            Add attendee
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Card>
                </Form>

                <Form form={topicsForm} layout="vertical" disabled={!minutesEditable}>
                  <Form.List name="topics">
                    {(fields) => (
                      <>
                        {fields.map((field) => {
                          const title = topicsForm.getFieldValue(['topics', field.name, 'title']);
                          return (
                            <Card
                              key={field.key}
                              size="small"
                              title={title || `Topic ${field.name + 1}`}
                              style={{ marginBottom: 12 }}
                            >
                              <Form.Item name={[field.name, 'id']} hidden>
                                <Input />
                              </Form.Item>
                              <Row gutter={16}>
                                <Col span={24}>
                                  <Form.Item name={[field.name, 'overview_comments']} label="Overview / Comments">
                                    <TextArea rows={2} />
                                  </Form.Item>
                                </Col>
                                <Col span={24}>
                                  <Form.Item name={[field.name, 'action_planned']} label="Action Planned (if any)">
                                    <TextArea rows={2} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item name={[field.name, 'action_target_date']} label="Action Target Date">
                                    <DatePicker style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item
                                    name={[field.name, 'action_responsibility']}
                                    label="Action Responsibility"
                                  >
                                    <Input />
                                  </Form.Item>
                                </Col>
                                <Col span={24}>
                                  <Form.Item name={[field.name, 'additional_remarks']} label="Additional Remarks">
                                    <TextArea rows={2} />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Card>
                          );
                        })}
                      </>
                    )}
                  </Form.List>
                </Form>

                <Form form={minutesForm} layout="vertical" disabled={!minutesEditable}>
                  <Card title="Updated by (MR)" size="small">
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <Form.Item name="minutes_updated_by_name" label="Name">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="minutes_updated_by_designation" label="Designation">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="minutes_updated_date" label="Date">
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Form>

                {minutesEditable ? (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saveMinutes.isPending}
                    onClick={() => saveMinutes.mutate()}
                  >
                    Save minutes
                  </Button>
                ) : (
                  <Alert type="info" showIcon message="Minutes are editable after agenda approval (Minutes In Progress)." />
                )}
              </Space>
            ),
          },
          {
            key: 'actions',
            label: 'Actions',
            children: (
              <Form form={actionsForm} layout="vertical" disabled={!canEdit || data.status === 'Closed' || data.status.startsWith('Pending')}>
                <Form.List name="actions">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field) => {
                        const capaId = actionsForm.getFieldValue(['actions', field.name, 'capa_id']);
                        const actionId = actionsForm.getFieldValue(['actions', field.name, 'id']);
                        return (
                          <Card key={field.key} size="small" style={{ marginBottom: 12 }} title={`Action ${field.name + 1}`}>
                            <Form.Item name={[field.name, 'id']} hidden>
                              <Input />
                            </Form.Item>
                            <Form.Item name={[field.name, 'capa_id']} hidden>
                              <Input />
                            </Form.Item>
                            <Form.Item name={[field.name, 'description']} label="Description" rules={[{ required: true }]}>
                              <TextArea rows={2} />
                            </Form.Item>
                            <Row gutter={16}>
                              <Col xs={24} md={8}>
                                <Form.Item name={[field.name, 'owner_name']} label="Responsibility">
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Form.Item name={[field.name, 'due_date']} label="Target date">
                                  <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Form.Item name={[field.name, 'status']} label="Status">
                                  <Input placeholder="Open / Closed" />
                                </Form.Item>
                              </Col>
                              <Col span={24}>
                                <Form.Item name={[field.name, 'effectiveness_notes']} label="Effectiveness notes">
                                  <TextArea rows={2} />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Space>
                              <Button danger type="link" onClick={() => remove(field.name)}>
                                Remove
                              </Button>
                              {capaId ? (
                                <Link to={`/qms/capas/${capaId}`}>Open CAPA</Link>
                              ) : actionId && canEdit ? (
                                <Button
                                  type="link"
                                  loading={createCapa.isPending}
                                  onClick={() =>
                                    createCapa.mutate({
                                      id: actionId,
                                      description: actionsForm.getFieldValue(['actions', field.name, 'description']),
                                      due_date: actionsForm.getFieldValue(['actions', field.name, 'due_date'])
                                        ? actionsForm
                                            .getFieldValue(['actions', field.name, 'due_date'])
                                            .format('YYYY-MM-DD')
                                        : null,
                                    })
                                  }
                                >
                                  Create CAPA
                                </Button>
                              ) : null}
                            </Space>
                          </Card>
                        );
                      })}
                      <Button type="dashed" onClick={() => add({ description: '', status: 'Open' })} block>
                        Add action
                      </Button>
                    </>
                  )}
                </Form.List>
                {canEdit && data.status !== 'Closed' && !String(data.status).startsWith('Pending') ? (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    style={{ marginTop: 12 }}
                    loading={saveActions.isPending}
                    onClick={() => saveActions.mutate()}
                  >
                    Save actions
                  </Button>
                ) : null}
              </Form>
            ),
          },
          {
            key: 'approvals',
            label: 'Approvals',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {(data.status === 'Pending Agenda Approval' || data.agenda_approval_instance_id) && (
                  <Card title="Agenda approval">
                    <ApprovalPanel
                      entityType={ENTITY_TYPE_MANAGEMENT_REVIEW_AGENDA}
                      entityId={data.id}
                      extraInvalidateQueryKeys={[['management-reviews'], ['management-reviews', id]]}
                    />
                  </Card>
                )}
                {(data.status === 'Pending Minutes Approval' || data.minutes_approval_instance_id) && (
                  <Card title="Minutes approval">
                    <ApprovalPanel
                      entityType={ENTITY_TYPE_MANAGEMENT_REVIEW_MINUTES}
                      entityId={data.id}
                      extraInvalidateQueryKeys={[['management-reviews'], ['management-reviews', id]]}
                    />
                  </Card>
                )}
                {data.status === 'Draft' || data.status === 'Agenda Approved' || data.status === 'Minutes In Progress' ? (
                  <Alert
                    type="info"
                    showIcon
                    message="Submit the agenda or minutes to start an Approval Runtime instance for Top Management."
                  />
                ) : null}
                {data.status === 'Closed' ? (
                  <Alert type="success" showIcon message="Meeting closed — minutes approved by Top Management." />
                ) : null}
              </Space>
            ),
          },
          {
            key: 'attachments',
            label: 'Attachments',
            children: (
              <ProcurementAttachmentsCard
                entityType={DOCUMENT_ENTITY_TYPE_MANAGEMENT_REVIEW}
                entityId={data.id}
                queryKeyPrefix={['documents', 'management-review']}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default ManagementReviewDetailPage;
